"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
const LIME = '#ceff1a';

const items = [
    {
        title: 'GA4',
        desc: 'Structure analytics around meaningful events, conversions, audiences and user journeys.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="18" width="6" height="9" rx="1.5" stroke="currentColor" strokeWidth="2" />
                <rect x="13" y="11" width="6" height="16" rx="1.5" stroke="currentColor" strokeWidth="2" />
                <rect x="21" y="5" width="6" height="22" rx="1.5" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        title: 'Google Tag Manager',
        desc: 'Build a cleaner and more manageable tracking framework across your website and marketing platforms.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <path d="M16 3l13 13-13 13L3 16 16 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        title: 'Conversion Tracking',
        desc: 'Measure the actions that matter — from enquiries and calls to purchases, signups and qualified leads.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
                <path d="M11 16l3.5 3.5L21 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Meta Conversion API',
        desc: "Improve the flow of conversion signals between your website and Meta's advertising ecosystem where appropriate.",
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <path d="M4 20c0-4.5 2-8 5-8 2 0 3.5 2 5 5 1.5-3.5 3-5 5-5 3 0 5 3.5 5 8 0 2-1 4-3 4s-3-2-4-4c-1 2-2 4-4 4s-4-2-4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Attribution',
        desc: 'Analyse how different channels and touchpoints contribute to the customer journey instead of evaluating every channel in isolation.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <circle cx="6" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="26" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="26" r="3" stroke="currentColor" strokeWidth="2" />
                <path d="M8.5 9.5L13.5 14M18.5 14L23.5 9.5M16 19v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'Marketing Dashboards',
        desc: 'Bring important marketing data together into reporting that is easier to understand and act on.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="5" width="24" height="22" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M4 12h24" stroke="currentColor" strokeWidth="2" />
                <path d="M10 17v5M16 15v7M22 18v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
];

function TableRow({ item, index }: { item: typeof items[0]; index: number }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
            className="group grid grid-cols-[52px_minmax(0,240px)_minmax(0,1fr)] md:grid-cols-[64px_260px_minmax(0,1fr)] transition-colors duration-200 hover:bg-white/[0.03]"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
            <div className="flex items-center justify-center py-4" style={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-xs" style={{ fontFamily: MONO, color: LIME }}>
                    {String(index + 1).padStart(2, '0')}
                </span>
            </div>
            <div className="flex items-center gap-2.5 py-4 px-4" style={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ color: LIME }} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                    {item.icon}
                </span>
                <span className="text-sm md:text-base font-bold" style={{ fontFamily: CLASH, color: '#ffffff' }}>
                    {item.title}
                </span>
            </div>
            <div className="flex items-center py-4 px-4">
                <p className="text-xs md:text-sm leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.5)' }}>
                    {item.desc}
                </p>
            </div>
        </motion.div>
    );
}

export default function AnalyticsWhatWeManage() {
    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }} />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-10 relative z-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: LIME, fontFamily: CLASH }}
                        >
                            What We Cover
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: '#ffffff' }}
                        >
                            What We Set Up
                        </motion.h2>
                    </div>
                    <span className="text-sm" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>
                        6 areas we cover
                    </span>
                </div>

                <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="grid grid-cols-[52px_minmax(0,240px)_minmax(0,1fr)] md:grid-cols-[64px_260px_minmax(0,1fr)]" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                        <div className="py-3 flex items-center justify-center" style={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                            <span className="text-[10px]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>#</span>
                        </div>
                        <div className="py-3 px-4" style={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                            <span className="text-[10px] uppercase tracking-[0.1em]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>tool</span>
                        </div>
                        <div className="py-3 px-4">
                            <span className="text-[10px] uppercase tracking-[0.1em]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>purpose</span>
                        </div>
                    </div>

                    {items.map((it, i) => (
                        <TableRow key={it.title} item={it} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
